import React, { useEffect, useState, useCallback } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import ajaxService from "../../../services/ajax-service";
import moment from "moment";
import { calculateAvailableWidth, calculateModalWidth } from "../../../utils/widthCalculator";
import {
  Button,
  Card,
  Popconfirm,
  message,
  Space,
  Breadcrumb,
  Tag,
  Descriptions,
  Spin,
  Modal,
  Form,
  Input,
  Row,
  Col,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  HomeOutlined,
  DeleteFilled,
} from "@ant-design/icons";

const QuestionDetails = () => {
  const { id } = useParams();
  const history = useHistory();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [cardWidth, setCardWidth] = useState('100%');
  const [modalWidth, setModalWidth] = useState('35%');

  const [showEditModal, setShowEditModal] = useState(false);
  const [form] = Form.useForm();

  const fetchData = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await ajaxService.get(`/interest/${id}`);
      if (response.success) {
        setData(response.data);
      } else {
        setError("Failed to fetch data");
      }
    } catch (err) {
      setError("Error fetching data");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDelete = useCallback(async () => {
    try {
      const response = await ajaxService.delete("/interest/", id);
      if (response.success) {
        message.success("Interest deleted successfully");
        history.push("/interest");
      } else {
        message.error("Failed to delete interest");
      }
    } catch (err) {
      console.error("Delete error:", err);
      message.error("Error deleting interest");
    }
  }, [id, history]);

  const openEditModal = () => {
    form.setFieldsValue({
      name: data.name || data.question,
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (values) => {
    const payload = {
      name: values.name,
      type: 1,
    };

    try {
      const response = await ajaxService.put("/interest/", id, payload);
      if (response.success) {
        message.success("Interest updated successfully");
        setData(response.data); // update details instantly
        fetchData(id);
        setShowEditModal(false);
      } else {
        message.error("Failed to update interest");
      }
    } catch (err) {
      console.error("Update error:", err);
      message.error("Error updating interest");
    }
  };

  useEffect(() => {
    if (id) fetchData(id);
  }, [id, fetchData]);

  // Listen for sidebar collapse state and calculate card width
  useEffect(() => {
    const updateWidth = () => {
      const width = calculateAvailableWidth(sidebarCollapsed, 40);
      setCardWidth(width);
      setModalWidth(calculateModalWidth('35%'));
    };
    
    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
      updateWidth();
    };
    
    window.addEventListener('sidebarCollapse', handleSidebarCollapse);
    window.addEventListener('resize', updateWidth);
    updateWidth();
    
    return () => {
      window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
      window.removeEventListener('resize', updateWidth);
    };
  }, [sidebarCollapsed]);

  if (loading) {
    return (
      <div className="page-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', padding: 'var(--spacing-2xl)' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) return (
    <div className="page-container" style={{ padding: 'var(--spacing-xl)', textAlign: 'center' }}>
      <p style={{ color: 'var(--error-red)' }}>{error}</p>
    </div>
  );
  if (!data) return (
    <div className="page-container" style={{ padding: 'var(--spacing-xl)', textAlign: 'center' }}>
      <p style={{ color: 'var(--text-gray)' }}>No data found</p>
    </div>
  );

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Header Section */}
      <div className="module-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-lg)' }}>
          <Breadcrumb
            items={[
              {
                title: (
                  <Link to="/dashboard" style={{ color: 'var(--text-white)' }}>
                    <HomeOutlined /> Home
                  </Link>
                ),
              },
              { title: <Link to="/interest" style={{ color: 'var(--text-white)' }}>Interest</Link> },
              {
                title: <span style={{ color: 'var(--text-gray)', maxWidth: 200, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {data?.name || data?.question ? (data.name || data.question).substring(0, 20) + '...' : "Details"}
                </span>,
              },
            ]}
          />
          <Space>
            <Button
              type="primary"
              ghost
              icon={<EditOutlined />}
              onClick={openEditModal}
              style={{
                background: 'var(--primary-purple)',
                borderColor: 'var(--primary-purple)',
                color: 'white',
              }}
            >
              Edit
            </Button>

            <Popconfirm
              title="Are you sure to delete this question?"
              onConfirm={handleDelete}
              okText="Yes"
              cancelText="No"
            >
              <Button 
                danger 
                icon={<DeleteOutlined />}
                style={{
                  background: 'var(--error-red)',
                  borderColor: 'var(--error-red)',
                }}
              >
                Delete
              </Button>
            </Popconfirm>

            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => history.push("/interest")}
              style={{
                background: 'var(--dark-bg-card)',
                borderColor: 'rgba(254, 2, 125, 0.3)',
                color: 'var(--text-white)',
              }}
            >
              Back
            </Button>
          </Space>
        </div>
        <h1 className="module-title">Interest Details</h1>
        <p className="module-subtitle">View and manage interest information</p>
      </div>

      <Card 
        style={{
          width: typeof cardWidth === 'string' ? cardWidth : `${cardWidth}px`,
          maxWidth: '100%',
          boxShadow: 'var(--shadow-card)',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--dark-bg-card)',
          border: '1px solid rgba(254, 2, 125, 0.2)',
        }}
      >
        {/* Details */}
        <Descriptions
          bordered
          column={1}
          labelStyle={{ fontWeight: "bold", width: "200px", color: 'var(--text-white)' }}
        >
          <Descriptions.Item label="ID"><span style={{ color: 'var(--text-white)' }}>{data.id}</span></Descriptions.Item>
          <Descriptions.Item label="Interest">
            <span style={{ color: 'var(--text-white)' }}>{data.name || data.question || "N/A"}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={data.status === 1 ? "green" : "red"}>
              {data.status === 1 ? "Active" : "Inactive"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            <span style={{ color: 'var(--text-white)' }}>
              {data.created_at
                ? moment(data.created_at).format("YYYY-MM-DD HH:mm:ss")
                : "N/A"}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            <span style={{ color: 'var(--text-white)' }}>
              {data.updated_at
                ? moment(data.updated_at).format("YYYY-MM-DD HH:mm:ss")
                : "N/A"}
            </span>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Edit Modal */}
      <Modal
        title={<span style={{ color: 'var(--text-white)' }}>Edit Interest</span>}
        open={showEditModal}
        onCancel={() => setShowEditModal(false)}
        footer={null}
        className="modal-overlay"
        width={modalWidth}
        centered
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label={<span style={{ color: 'var(--text-white)', fontWeight: 600 }}>Interest Name</span>}
                name="name"
                rules={[{ required: true, message: "Enter interest name" }]}
              >
                <Input 
                  placeholder="Enter interest name" 
                  className="input-field"
                  addonBefore="Interest "
                />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ textAlign: "center", marginTop: 'var(--spacing-xl)' }}>
            <Button 
              type="primary" 
              htmlType="submit"
              className="btn-primary"
              style={{ minWidth: '120px' }}
            >
              Update
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default QuestionDetails;
